import { cloneDeep } from 'lodash';
import { EDeformationImgType } from './states/deformation-img';
import { CornerPoint } from './types';

export const isAngleInRange = (vector: [number, number]) => {
    const [x, y] = vector;

    // 计算向量与X轴夹角（弧度）
    const angleRad = Math.atan2(y, x);

    // 转换为角度（-180°到180°）
    let angleDeg = angleRad * (180 / Math.PI);

    // 将角度规范化为0-360°范围
    if (angleDeg < 0) angleDeg += 360;

    // 判断是否在 [-45°,45°] 或 [135°,225°] 范围内
    return (
        (angleDeg >= 0 && angleDeg <= 45) || // 第一象限
        (angleDeg >= 315 && angleDeg <= 360) || // 等效于-45°-0°
        (angleDeg >= 135 && angleDeg <= 225) // 第二、三象限
    );
};

/**
 * 计算点 (px, py) 到向量 AB（由 A(ax, ay) 到 B(bx, by)）的垂直距离
 * @param  px - 点 P 的 x 坐标
 * @param  py - 点 P 的 y 坐标
 * @param  ax - 点 A 的 x 坐标（向量起点）
 * @param  ay - 点 A 的 y 坐标
 * @param  bx - 点 B 的 x 坐标（向量终点）
 * @param  by - 点 B 的 y 坐标
 * @returns 点 P 到向量 AB 的垂直距离
 */
export const pointToVectorDistance = (px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
    // 向量 AB = B - A
    const vx = bx - ax;
    const vy = by - ay;

    // 向量 AP = P - A
    const wx = px - ax;
    const wy = py - ay;

    // 计算 AB 和 AP 的二维叉积：v × w = vx * wy - vy * wx
    // 这个值代表 P 到直线 AB 的有向面积（方向）
    const cross = vx * wy - vy * wx;

    // 向量 AB 的长度（模）
    const length = Math.sqrt(vx * vx + vy * vy);

    // 距离公式：d = |v × w| / |v|
    const distance = cross / length;
    return distance;
};

// 判断是否超出边界判断 超过外框 或者三个点不能连成一条直线
const isOverflow = (points: CornerPoint[], maxWidth: number, maxHeight: number, index: number) => {
    const isOverflowLayer = points.some((point) => {
        return point.left < 0 || point.left > maxWidth || point.top < 0 || point.top > maxHeight;
    });

    if (isOverflowLayer) {
        return true;
    }

    const indexs: number[] = [];

    if (index === 0) {
        indexs.push(0);
        indexs.push(1);
        indexs.push(3);

        indexs.push(1);
        indexs.push(2);
        indexs.push(0);

        indexs.push(3);
        indexs.push(0);
        indexs.push(2);
    } else if (index === 1) {
        indexs.push(1);
        indexs.push(2);
        indexs.push(0);

        indexs.push(0);
        indexs.push(1);
        indexs.push(3);

        indexs.push(2);
        indexs.push(3);
        indexs.push(1);
    } else if (index === 2) {
        indexs.push(2);
        indexs.push(3);
        indexs.push(1);

        indexs.push(3);
        indexs.push(0);
        indexs.push(2);

        indexs.push(1);
        indexs.push(2);
        indexs.push(0);
    } else {
        indexs.push(3);
        indexs.push(0);
        indexs.push(2);

        indexs.push(0);
        indexs.push(1);
        indexs.push(3);

        indexs.push(2);
        indexs.push(3);
        indexs.push(1);
    }
    if (
        pointToVectorDistance(
            points[indexs[0]].left,
            points[indexs[0]].top,
            points[indexs[1]].left,
            points[indexs[1]].top,
            points[indexs[2]].left,
            points[indexs[2]].top,
        ) < 30
    ) {
        return true;
    }

    if (
        pointToVectorDistance(
            points[indexs[3]].left,
            points[indexs[3]].top,
            points[indexs[4]].left,
            points[indexs[4]].top,
            points[indexs[5]].left,
            points[indexs[5]].top,
        ) < 30
    ) {
        return true;
    }

    if (
        pointToVectorDistance(
            points[indexs[6]].left,
            points[indexs[6]].top,
            points[indexs[7]].left,
            points[indexs[7]].top,
            points[indexs[8]].left,
            points[indexs[8]].top,
        ) < 30
    ) {
        return true;
    }

    return false;
};

export const moveLeftTop = (
    newPoints: CornerPoint[],
    moveX: number,
    moveY: number,
    currentType: EDeformationImgType,
    maxWidth: number,
    maxHeight: number,
) => {
    const points = cloneDeep(newPoints);
    if (currentType === EDeformationImgType.PERSPECTIVE && isAngleInRange([moveX, moveY])) {
        points[0].left += moveX;
        points[1].left -= moveX;
    } else if (currentType === EDeformationImgType.PERSPECTIVE) {
        points[0].top += moveY;
        points[3].top -= moveY;
    } else {
        points[0].left += moveX;
        points[0].top += moveY;
    }

    if (isOverflow(points, maxWidth, maxHeight, 0)) {
        return;
    } else {
        newPoints.forEach((point, index) => {
            newPoints[index] = points[index];
        });
    }
};

export const moveRightTop = (
    newPoints: CornerPoint[],
    moveX: number,
    moveY: number,
    currentType: EDeformationImgType,
    maxWidth: number,
    maxHeight: number,
) => {
    const points = cloneDeep(newPoints);
    if (currentType === EDeformationImgType.PERSPECTIVE && isAngleInRange([moveX, moveY])) {
        points[1].left += moveX;
        points[0].left -= moveX;
    } else if (currentType === EDeformationImgType.PERSPECTIVE) {
        points[1].top += moveY;
        points[2].top -= moveY;
    } else {
        points[1].top += moveY;
        points[1].left += moveX;
    }

    if (isOverflow(points, maxWidth, maxHeight, 1)) {
        return;
    } else {
        newPoints.forEach((point, index) => {
            newPoints[index] = points[index];
        });
    }
};

export const moveRightBottom = (
    newPoints: CornerPoint[],
    moveX: number,
    moveY: number,
    currentType: EDeformationImgType,
    maxWidth: number,
    maxHeight: number,
) => {
    const points = cloneDeep(newPoints);
    if (currentType === EDeformationImgType.PERSPECTIVE && isAngleInRange([moveX, moveY])) {
        points[2].left += moveX;
        points[3].left -= moveX;
    } else if (currentType === EDeformationImgType.PERSPECTIVE) {
        points[2].top += moveY;
        points[1].top -= moveY;
    } else {
        points[2].left += moveX;
        points[2].top += moveY;
    }

    if (isOverflow(points, maxWidth, maxHeight, 2)) {
        return;
    } else {
        newPoints.forEach((point, index) => {
            newPoints[index] = points[index];
        });
    }
};

export const moveLeftBottom = (
    newPoints: CornerPoint[],
    moveX: number,
    moveY: number,
    currentType: EDeformationImgType,
    maxWidth: number,
    maxHeight: number,
) => {
    const points = cloneDeep(newPoints);
    if (currentType === EDeformationImgType.PERSPECTIVE && isAngleInRange([moveX, moveY])) {
        points[3].left += moveX;
        points[2].left -= moveX;
    } else if (currentType === EDeformationImgType.PERSPECTIVE) {
        points[3].top += moveY;
        points[0].top -= moveY;
    } else {
        points[3].left += moveX;
        points[3].top += moveY;
    }

    if (isOverflow(points, maxWidth, maxHeight, 3)) {
        return;
    } else {
        newPoints.forEach((point, index) => {
            newPoints[index] = points[index];
        });
    }
};
